<template>
    <DsModalDialog
        v-model="isQuestModalOpen"
        theme="dark"
        :css-class="[
            'bonusomat-modal',
            'bonusomat-modal--wider',
            areTasksGamesOpened ? 'bonusomat-quest-games' : 'bonusomat-quest',
        ]"
        :top-divider-visible="true"
        :bottom-divider-visible="false"
        :content-scrollable="true"
        :has-dialog-close-btn="!areTasksGamesOpened"
        @modal-closed="closeModalAndGoBackToTasks()"
        ref="questModalRef"
    >
        <template #top v-if="areTasksGamesOpened">
            <DsButton
                icon-left="arrow-left"
                theme="dark"
                type="interaction"
                size="lg"
                @click="goBackToTasks"
                class="bonusomat-quest__games-back-btn"
            >
                Zpět
            </DsButton>
            <ModalHeadline
                :headline="gamesSectionHeadline"
                class="bonusomat-quest__bonusomat-quest__games-modal-title"
            />
            <div class="bonusomat-modal__games-modal-description" v-html="openedGamesTask.description"></div>
        </template>
        <template #top v-else>
            <ModalHeadline :headline="openedQuest?.title" class="bonusomat-quest__title" />
        </template>
        <template #content v-if="areTasksGamesOpened">
            <QuestDetailGames
                :task="openedGamesTask"
                :quest-name="openedQuest?.title"
                @close-modal="closeModalAndGoBackToTasks()"
            />
        </template>
        <template #content v-else>
            <div class="bonusomat-quest__image-wrapper">
                <DsBadge v-if="openedQuest.badge" :text="openedQuest.badge" class="bonusomat-quest__badge" />
                <WebpImage :src="openedQuest.image" alt="" css-class="bonusomat-quest__image" />
            </div>
            <p class="bonusomat-modal__description" v-html="openedQuest.description"></p>
            <DsDivider theme="dark" />
            <div class="bonusomat-quest__details">
                <div class="bonusomat-quest__details-item bonusomat-quest__details-item--yellow">
                    <BonusomatQuestAwardIcon :icon-name="openedQuest.awardIcon" />
                    <div class="bonusomat-quest__award-text">
                        {{ openedQuest.award }}
                    </div>
                </div>
                <div class="bonusomat-quest__details-items-group">
                    <div class="bonusomat-quest__details-item">
                        <DsIcon name="bet-list" :width="24" :height="24" />
                        {{ totalTasksFormatted }}
                    </div>
                    <div class="bonusomat-quest__details-item" v-if="openedQuest.endDate">
                        <DsIcon name="time" :width="24" :height="24" />
                        {{ questTimeLabel }}
                    </div>
                </div>
            </div>
            <div class="bonusomat-quest__tasks">
                <QuestAccordion
                    v-if="hasAnyTask"
                    :tasks="sortedTasks"
                    :default-open-task-index="openedGamesTaskIndex"
                    theme="dark"
                    @show-task-games="(taskToShowGames : BonusomatQuestTask, taskIndex: number) =>
                showGamesForTask(taskToShowGames, taskIndex)"
                    :scrollTaskIntoView
                />
                <div v-else class="bonusomat-quest__no-tasks-box">Žádné úkoly k plnění</div>
            </div>
        </template>
        <template #bottom>
            <div
                class="bonusomat-modal__footer"
                :class="{
                    'bonusomat-modal__footer--completed': isCompleted,
                    'bonusomat-modal__footer--active': isActive,
                }"
            >
                <div class="bonusomat-modal__footer__progress-wrapper">
                    <BonusomatProgress
                        :key="questProgressKey"
                        :progress="questProgress"
                        :is-completed="openedQuest.completed"
                        :show-tracker-even-if-completed="true"
                        show-percentage-number
                    />
                    <div class="bonusomat-modal__footer__texts">
                        <div class="bonusomat-modal__footer__state">{{ footerQuestState }}</div>
                        <div
                            v-if="!isCompleted && hasAnyTask && remainingTasks"
                            class="bonusomat-modal__footer__remaining-tasks"
                        >
                            <span>{{ remainingInclined }}</span>
                            <span class="bonusomat-modal__footer__tasks-count">{{ remainingTasksFormatted }}</span>
                        </div>
                    </div>
                </div>
                <div class="bonusomat-modal__footer__reward" v-if="isCompleted">
                    Vaše výhra
                    <div
                        class="bonusomat-quest__details-item bonusomat-quest__details-item--yellow"
                        :class="completedQuestRewardInfoClass"
                    >
                        <BonusomatQuestAwardIcon :icon-name="openedQuest.awardIcon" />
                        <div class="bonusomat-quest__award-text">
                            {{ openedQuest.award }}
                        </div>
                    </div>
                </div>
                <DsButton
                    theme="dark"
                    size="lg"
                    :type="footerButtonType"
                    :icon-left="footerButtonIcon"
                    :is-disabled="!isPlayable"
                    @click="footerButtonClickHandler"
                >
                    {{ footerButtonText }}
                </DsButton>
            </div>
        </template>
    </DsModalDialog>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import DsBadge from "@sazka/design-system/src/badge/ds-badge.vue";
import DsButton from "@sazka/design-system/src/button/ds-button.vue";
import DsDivider from "@sazka/design-system/src/divider/ds-divider.vue";
import DsIcon from "@sazka/design-system/src/icon/ds-icon.vue";
import WebpImage from "@sazka/design-system/src/image/webp-image.vue";
import DsModalDialog from "@sazka/design-system/src/modal-dialog/ds-modal-dialog.vue";
import ModalHeadline from "@sazka/design-system/src/top-slot/ds-top-subheadline.vue";
import { SvgIcons } from "@sazka/design-system/src/icon/types";
import { DsModalDialogReferenceType } from "@sazka/design-system/src/modal-dialog/ds-modal-dialog-models";
import { BonusomatDatalayer } from "@/casino/bonusomat/utils/analytics/bonusomat-datalayer";
import { useLoyaltyPlayerStatus } from "@/loyalty/composables/use-loyalty-player-status";
import BonusomatQuestAwardIcon from "./bonusomat-quest-award-icon.vue";
import QuestAccordion from "./bonusomat-quest-detail-accordion.vue";
import QuestDetailGames from "./bonusomat-quest-detail-games.vue";
import useBonusomatData from "../../composables/use-bonusomat-data";
import { useQuestDetails } from "../../composables/use-quest-details";
import { BonusomatQuestTask } from "../../models/bonusomat-quest-detail";
import { BonusomatUtils } from "../../utils/bonusomat-utils";
import BonusomatProgress from "../shared/bonusomat-progress.vue";

const { bonusomatPlayerStatus } = useLoyaltyPlayerStatus();
const { bonusomatPlayerData, remainingSpinsCount } = bonusomatPlayerStatus;

const {
    isQuestModalOpen,
    openedQuest,
    questCardProgressLoadCounter,
    questDetailProgressLoadCounter,
    gamesData,
    openedGamesTaskIndex,
    activateQuest,
    deactivateQuest,
    loadGamesData,
} = useBonusomatData();

const { isActive, isCompleted, isBonusAward, remainingTasks, questTimeLabel, questProgress, isPlayable } =
    useQuestDetails(openedQuest);

const gamesSectionHeadline = "Úkol splníte hraním vybraných her";

const questModalRef = ref<DsModalDialogReferenceType>();
const areTasksGamesOpened = ref(false);

const questProgressKey = computed(() => {
    return "questDetailProgress-" + questDetailProgressLoadCounter.value;
});

const sortedTasks = computed(() => {
    return openedQuest.value.tasks
        ?.slice()
        .sort((a, b) => (a.progress < 100 && b.progress >= 100 ? -1 : a.progress >= 100 && b.progress < 100 ? 1 : 0));
});

const completedQuestRewardInfoClass = computed(() => {
    return openedQuest.value.award.length > 16 ? "bonusomat-quest__details-item--long-text" : "";
});

const hasAnyTask = computed(() => {
    return !!openedQuest.value.tasks?.length;
});

const totalTasksFormatted = computed(() => {
    return openedQuest.value ? BonusomatUtils.getTaskCountLabel(openedQuest.value.totalTasks) : "";
});

const remainingTasksFormatted = computed(() => {
    return openedQuest.value ? BonusomatUtils.getTaskCountLabel(remainingTasks.value) : "";
});

const footerQuestState = computed(() => {
    if (isActive.value) {
        return "Aktivní";
    } else if (isCompleted.value) {
        return "Dokončeno";
    } else {
        return "Neaktivní";
    }
});

const remainingInclined = computed(() => {
    switch (remainingTasks.value) {
        case 2:
        case 3:
        case 4:
            return "Zbývají";
        default:
            return "Zbývá";
    }
});

const footerButtonText = computed(() => {
    if (isCompleted.value) {
        return isBonusAward.value ? "Zobrazit výhru" : "Zavřít";
    } else if (isActive.value) {
        return "Deaktivovat";
    } else if (!hasAnyTask.value) {
        return "Získat bonus";
    } else {
        return "Začít hrát";
    }
});

const footerButtonType = computed(() => {
    if (isActive.value) {
        return "error";
    } else if (isCompleted.value && !isBonusAward.value) {
        return "secondary";
    } else {
        return "primary";
    }
});

const footerButtonIcon = computed<SvgIcons>(() => {
    if (isActive.value) {
        return "cross";
    } else {
        return null;
    }
});

const openedGamesTask = ref<BonusomatQuestTask>(null);

const showGamesForTask = async (taskToShowGames: BonusomatQuestTask, taskIndex: number) => {
    if (!gamesData.value?.length) {
        await loadGamesData();
    }
    questDetailProgressLoadCounter.value++;

    openedGamesTask.value = taskToShowGames;
    openedGamesTaskIndex.value = taskIndex;
    areTasksGamesOpened.value = true;
    questModalRef.value.scrollContent();
};

const goBackToTasks = () => {
    if (areTasksGamesOpened.value) {
        BonusomatDatalayer.pushCloseQuestGames(openedGamesTask.value?.title);
    }
    questDetailProgressLoadCounter.value++;

    areTasksGamesOpened.value = false;
    openedGamesTask.value = null;
};

const closeModalAndGoBackToTasks = () => {
    isQuestModalOpen.value = false;
    goBackToTasks();
};

const footerButtonClickHandler = async () => {
    const executedFromGamesSection = areTasksGamesOpened.value;

    if (isCompleted.value) {
        closeModalAndGoBackToTasks();

        if (!isBonusAward.value) {
            return;
        }

        ng_fw.showMyBonuses();

        BonusomatDatalayer.pushShowWinnings(openedQuest.value.title);
    } else if (isActive.value) {
        closeModalAndGoBackToTasks();

        try {
            await deactivateQuest(executedFromGamesSection ? "questDetailGames" : "questDetail");
        } catch (ex: unknown) {
            console.error(ex);
        }
    } else {
        try {
            await activateQuest(openedQuest.value.id);
        } finally {
            BonusomatDatalayer.pushStartQuest(
                openedQuest.value.title,
                "questDetail",
                bonusomatPlayerData.value.points,
                remainingSpinsCount.value
            );
            setTimeout(() => {
                questCardProgressLoadCounter.value++;
            }, 300);
        }
    }
};

const scrollTaskIntoView = ref(false);
onMounted(() => {
    scrollTaskIntoView.value = true;
});
</script>
